import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  TextField, Button, Typography, Box, Alert, IconButton,
  InputAdornment, CircularProgress
} from '@mui/material';
import { Lock, Person, Visibility, VisibilityOff, Language } from '@mui/icons-material';
import LanguageSelector from './LanguageSelector';

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { search } = useLocation();
  const { t } = useTranslation();

  const isExpired = new URLSearchParams(search).get('expired') === 'true';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post('/admin/login', { username, password });
      
      if (response.data.success) {
        localStorage.setItem('adminToken', response.data.token);
        localStorage.setItem('adminData', JSON.stringify(response.data.admin));
        onLogin();
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || t('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: '#f0f2f8',
      }}
    >
      {/* ── Left Decorative Panel ── */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flex: '0 0 50%',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(145deg, #071a07 0%, #116611 55%, #338800 100%)',
          position: 'relative',
          overflow: 'hidden',
          p: 6,
        }}
      >
        {/* Decorative circles */}
        {[
          { size: 320, top: -80, right: -80, opacity: 0.07 },
          { size: 200, bottom: 40, left: -60, opacity: 0.06 },
          { size: 100, top: '50%', left: '60%', opacity: 0.1 },
        ].map((c, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              width: c.size,
              height: c.size,
              borderRadius: '50%',
              border: '2px solid rgba(85,170,34,0.4)',
              top: c.top,
              bottom: c.bottom,
              left: c.left,
              right: c.right,
              opacity: c.opacity,
              background: `radial-gradient(circle, rgba(85,170,34,0.12) 0%, transparent 70%)`,
            }}
          />
        ))}

        <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          {/* Logo */}
          <Box
            component="img"
            src="/draingo-logo.jpeg"
            alt="DrainGo Logo"
            sx={{
              width: 110,
              height: 110,
              borderRadius: '28px',
              objectFit: 'cover',
              mx: 'auto',
              mb: 3,
              display: 'block',
              boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
              border: '3px solid rgba(255,255,255,0.2)',
            }}
          />

          <Typography
            variant="h3"
            sx={{
              fontWeight: 900,
              background: 'linear-gradient(135deg, #ffffff 0%, #99CC77 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
              letterSpacing: '-1px',
            }}
          >
            DrainGo
          </Typography>

          <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '1rem', maxWidth: 300, lineHeight: 1.7 }}>
            Your complete drain service management platform
          </Typography>

          {/* Stats row */}
          <Box sx={{ display: 'flex', gap: 4, mt: 5, justifyContent: 'center' }}>
            {[
              { value: '24/7', label: 'Support' },
              { value: '100%', label: 'Uptime' },
              { value: '∞', label: 'Scale' },
            ].map((s, i) => (
              <Box key={i} sx={{ textAlign: 'center' }}>
                <Typography sx={{ color: '#55AA22', fontWeight: 800, fontSize: '1.4rem', lineHeight: 1 }}>{s.value}</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', mt: 0.5 }}>{s.label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Right Form Panel ── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, md: 6 },
          bgcolor: '#ffffff',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          {/* Mobile brand */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 4 }}>
            <Box
              component="img"
              src="/draingo-logo.jpeg"
              alt="DrainGo Logo"
              sx={{ width: 40, height: 40, borderRadius: '10px', objectFit: 'cover' }}
            />
            <Typography variant="h5" fontWeight={800} sx={{ color: '#116611' }}>
              DrainGo Admin
            </Typography>
          </Box>

          <Box sx={{ position: 'absolute', top: 20, right: 20 }}>
            <LanguageSelector />
          </Box>
          <Typography variant="h4" fontWeight={800} sx={{ color: '#1a1a2e', mb: 0.5 }}>
            {t('welcomeBack')}
          </Typography>
          <Typography sx={{ color: '#78909c', mb: 4, fontSize: '0.95rem' }}>
            {t('signInToContinue')}
          </Typography>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: 2, '& .MuiAlert-icon': { alignItems: 'center' } }}
            >
              {error}
            </Alert>
          )}
          
          {isExpired && (
            <Alert
              severity="warning"
              sx={{ mb: 3, borderRadius: 2, '& .MuiAlert-icon': { alignItems: 'center' } }}
            >
              Your session has expired. Please login again to continue.
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              label={t('username')}
              variant="outlined"
              fullWidth
              margin="normal"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person sx={{ color: '#78909c', fontSize: '1.1rem' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: '#116611' },
                  '&.Mui-focused fieldset': { borderColor: '#116611' },
                },
                '& .MuiInputLabel-root.Mui-focused': { color: '#116611' },
              }}
            />

            <TextField
              label={t('password')}
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: '#78909c', fontSize: '1.1rem' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      sx={{ color: '#78909c' }}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: '#116611' },
                  '&.Mui-focused fieldset': { borderColor: '#116611' },
                },
                '& .MuiInputLabel-root.Mui-focused': { color: '#116611' },
              }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                mt: 3.5,
                py: 1.6,
                fontSize: '0.95rem',
                fontWeight: 700,
                background: 'linear-gradient(135deg, #116611 0%, #338800 100%)',
                borderRadius: '12px',
                boxShadow: '0 6px 20px rgba(17,102,17,0.35)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #338800 0%, #55AA22 100%)',
                  boxShadow: '0 8px 25px rgba(17,102,17,0.45)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : t('login')}
            </Button>
          </form>

          <Typography sx={{ mt: 4, textAlign: 'center', color: '#b0bec5', fontSize: '0.8rem' }}>
            DrainGo Admin Panel © 2025
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;